// PostHog for every product's app: usage events and session replay.
//
// One PostHog project serves every product, so everything sent from here
// carries `product` and `surface: 'app'`. Replays never contain customer
// content: every text node and every input is masked, for every product —
// one app holds customers' quotes and emails, another holds health entries,
// and CLAUDE.md's rule is "never log customer content". What a replay shows
// is layout, navigation and clicks. This is not configurable per product on
// purpose; unmasking would also be a privacy-policy change.
//
// Off without VITE_PUBLIC_POSTHOG_KEY and off on localhost (unless
// VITE_PUBLIC_POSTHOG_DEBUG=1). Nothing here may throw into the app: a
// blocked or failing tag is not the app's problem.

import type { PostHog } from 'posthog-js'

export type AnalyticsSetup = {
  key: string
  host: string
  product: string
  debug?: boolean
}

export function isTrackedHost(hostname: string): boolean {
  return (
    hostname !== '' &&
    hostname !== 'localhost' &&
    hostname !== '127.0.0.1' &&
    hostname !== '[::1]' &&
    !hostname.endsWith('.local') &&
    !hostname.endsWith('.localhost')
  )
}

/** What the app hands to posthog.init. Pure, so tests can pin the masking. */
export function analyticsOptions(setup: AnalyticsSetup) {
  return {
    api_host: setup.host,
    // First-party storage so a replay survives a reload; no third-party cookie.
    persistence: 'localStorage' as const,
    person_profiles: 'identified_only' as const,
    // A single-page app: one pageview per route change, not per load.
    capture_pageview: 'history_change' as const,
    autocapture: true,
    session_recording: { maskAllInputs: true, maskTextSelector: '*' },
    debug: setup.debug ?? false,
  }
}

export function analyticsRegister(setup: AnalyticsSetup) {
  return { product: setup.product, surface: 'app' as const }
}

/** Reads the build variables; null means "do not initialise". */
export function analyticsSetupFromEnv(
  env: Record<string, string | boolean | undefined>,
  hostname: string,
  product: string,
): AnalyticsSetup | null {
  const key = typeof env.VITE_PUBLIC_POSTHOG_KEY === 'string' ? env.VITE_PUBLIC_POSTHOG_KEY : ''
  const debug = env.VITE_PUBLIC_POSTHOG_DEBUG === '1'
  if (!key || !product) return null
  if (!debug && !isTrackedHost(hostname)) return null
  const host = typeof env.VITE_PUBLIC_POSTHOG_HOST === 'string' && env.VITE_PUBLIC_POSTHOG_HOST ? env.VITE_PUBLIC_POSTHOG_HOST : 'https://eu.i.posthog.com'
  return { key, host, product, debug }
}

// Loaded on demand: posthog-js is ~100 KB gzipped, so the app bundle does
// not carry it, and a build without a key never downloads it at all. Calls
// made before it has loaded are dropped, which only ever costs the first
// instant of a session.
let posthog: PostHog | null = null

export function initAnalytics(product: string): void {
  const setup = analyticsSetupFromEnv(import.meta.env as Record<string, string | boolean | undefined>, location.hostname, product)
  if (!setup) return
  import('posthog-js')
    .then(({ default: client }) => {
      client.init(setup.key, analyticsOptions(setup))
      client.register(analyticsRegister(setup))
      posthog = client
    })
    .catch(() => {
      // Never the app's failure.
    })
}

/** The events the shared shell sends. Products add their own names beside these. */
export const EVENTS = {
  checkoutStarted: 'checkout_started',
  portalOpened: 'portal_opened',
  localeChanged: 'locale_changed',
  signedIn: 'signed_in',
  signedOut: 'signed_out',
} as const

/** Send an event. A no-op when analytics is off, so callers never check. */
export function track(event: string, properties?: Record<string, string | number | boolean | null>): void {
  if (!posthog) return
  try {
    posthog.capture(event, properties)
  } catch {
    // Same.
  }
}

/** The signed-in person: the Supabase user id and their workspace — never the email. */
export function identify(userId: string, organizationId: string, product: string): void {
  if (!posthog) return
  try {
    posthog.identify(userId, { product })
    posthog.group('organization', organizationId, { product })
  } catch {
    // Same.
  }
}

/** Forget the person on sign-out so the next visitor on this browser is not them. */
export function resetIdentity(): void {
  if (!posthog) return
  try {
    posthog.reset()
  } catch {
    // Same.
  }
}
